import type { Section, TreeNode } from "idfweb-workbench";

import type { Disk } from "./board.ts";
import { NotFat, openFatPartition, type FatEntry, type FatPartition } from "./fat.ts";
import { valueFile } from "./nvs-format.ts";
import { NotNvs, readNvs, type NvsEntry, type NvsPartition } from "./nvs.ts";
import {
    DEFAULT_TABLE_OFFSET,
    findPartitionTable,
    formatSize,
    hex,
    NotATable,
    parsePartitionTable,
    SECTOR,
    subtypeName,
    TABLE_LENGTH,
    typeName,
    type Partition,
    type PartitionTable,
} from "./partition-table.ts";

/**
 * The explorer's Device section: what the connected board holds, read from
 * the board when a row is expanded. The page's README describes it.
 */

/** A scan for a table at an offset the build does not say ends here. */
export const SCAN_END = 0x20000;

/** Where a file of a partition's filesystem opens: `/device/<partition>/<path>`, read-only. */
export const DEVICE_FILES = "/device";

export interface DeviceAccess {
    /** The connected board, compared by identity: a new one is read again. */
    board(): object | undefined;
    disks(): Disk[];
    /**
     * Read through the device pane, which keeps other work off the board
     * meanwhile and shows the read as a task titled `title`.
     */
    read(disk: string, offset: number, length: number, title: string): Promise<Uint8Array>;
    /** CONFIG_PARTITION_TABLE_OFFSET of the last build, if there is one. */
    tableOffset(): Promise<number | undefined>;
    /** The section's rows changed; the page redraws the explorer. */
    changed(): void;
    /** Put a file read from the board where the editor opens its row's `path`. */
    putFile(path: string, data: Uint8Array): Promise<void>;
    /** Open a partition's NVS table in a tab, or show the tab when it is open. */
    openNvs(tab: NvsTab): void;
    /** Open a file `putFile` put in an editor tab. */
    openFile(path: string): void;
}

/** What was read of an NVS partition, as its tab shows it. */
export type NvsState =
    { state: "reading" } | { state: "read"; nvs: NvsPartition } | { state: "failed"; reason: string };

export interface NvsTab {
    id: string;
    title: string;
    /** Undefined when nothing was read from the board connected now. */
    state(): NvsState | undefined;
    /** Call `redraw` when the state changes, until the returned function runs. */
    watch(redraw: () => void): () => void;
    /** Open a string's or a blob's whole value in an editor tab. */
    open(entry: NvsEntry): void;
}

type TableState =
    | { state: "unread" }
    | { state: "reading" }
    | { state: "read"; offset: number; table: PartitionTable }
    | { state: "failed"; reason: string };

type FatState =
    | { state: "reading" }
    /** `put` holds the folders whose files are in `DEVICE_FILES`. */
    | { state: "read"; fat: FatPartition; put: Set<string> }
    | { state: "failed"; reason: string };

export class DeviceExplorer {
    readonly #access: DeviceAccess;
    #board: object | undefined;
    #table: TableState = { state: "unread" };
    /** What was read of each FAT partition, by its offset; dropped with the table. */
    #fats = new Map<number, FatState>();
    /** The same for NVS partitions, and the open tabs' redraws. */
    #nvs = new Map<number, NvsState>();
    #nvsTabs = new Set<() => void>();
    /**
     * Part of the rows' ids. A new board or a flash changes it, so an expanded
     * Partitions row comes back collapsed: only a person's expand resets a board.
     */
    #generation = 0;

    constructor(access: DeviceAccess) {
        this.#access = access;
    }

    section(): Section {
        return {
            id: "device",
            title: "Device",
            root: async () => this.#root(),
            actions: [{ icon: "refresh", title: "Read the Device Again", run: () => this.#reread() }],
        };
    }

    /** Drop what was read, as after a flash; the board is read again at the next expand. */
    forget(): void {
        if (this.#table.state === "reading") return;
        this.#table = { state: "unread" };
        this.#fats.clear();
        this.#clearNvs();
        this.#generation++;
        this.#access.changed();
    }

    /** Read again now what is shown expanded. */
    #reread(): void {
        const reads = [...this.#fats.values(), ...this.#nvs.values()];
        if (this.#table.state === "reading" || reads.some((read) => read.state === "reading")) return;
        this.#table = { state: "unread" };
        this.#fats.clear();
        this.#clearNvs();
        this.#access.changed();
    }

    #root(): TreeNode[] {
        const board = this.#access.board();
        if (board !== this.#board) {
            this.#board = board;
            this.#generation++;
            if (this.#table.state !== "reading") this.#table = { state: "unread" };
            this.#fats.clear();
            this.#clearNvs();
        }
        if (board === undefined) return [note("device:none", "No board connected")];
        const flash = this.#access.disks().find((disk) => disk.kind === "internal-nor");
        if (flash === undefined) return [note("device:none", "This board offers no flash to read")];
        const where = this.#table.state === "read" ? `table at ${hex(this.#table.offset)}` : undefined;
        return [
            {
                id: `device:partitions:${this.#generation}`,
                label: "Partitions",
                kind: "group",
                description: [flash.size === undefined ? undefined : formatSize(flash.size), where]
                    .filter(Boolean)
                    .join(", "),
                title: `The partition table in ${flash.label.toLowerCase()}.\nA board connected over USB resets to be read.`,
                children: async () => this.#partitions(flash.id),
            },
        ];
    }

    #partitions(disk: string): TreeNode[] {
        const table = this.#table;
        switch (table.state) {
            case "unread":
                void this.#read(disk);
                return [note("device:reading", "Reading…")];
            case "reading":
                return [note("device:reading", "Reading…")];
            case "failed":
                return [{ ...note("device:failed", `Could not read: ${table.reason}`), title: table.reason }];
            case "read":
                return table.table.partitions.map((partition) => ({
                    id: `device:partition:${hex(partition.offset)}`,
                    label: partition.label,
                    kind: "file",
                    description: `${typeName(partition.type)}, ${subtypeName(partition.type, partition.subtype)}`,
                    title: partitionTitle(partition),
                    badge: formatSize(partition.size),
                    ...(isFat(partition) ? this.#fatRow(disk, partition) : {}),
                    ...(isNvs(partition) ? this.#nvsRow(disk, partition) : {}),
                }));
        }
    }

    async #read(disk: string): Promise<void> {
        const board = this.#board;
        this.#table = { state: "reading" };
        let next: TableState;
        try {
            next = await readTable(this.#access, disk);
        } catch (e) {
            next = { state: "failed", reason: e instanceof Error ? e.message : String(e) };
        }
        // A board connected meanwhile is read when its row is expanded.
        this.#table = board === this.#board ? next : { state: "unread" };
        this.#access.changed();
    }

    /** A FAT partition's row expands into its files; the whole partition is read at the first expand. */
    #fatRow(disk: string, partition: Partition): Partial<TreeNode> {
        const fat = this.#fats.get(partition.offset);
        const read = fat?.state === "read" ? `\nFAT${fat.fat.volume.type}, ${fat.fat.layout}` : "";
        return {
            id: `device:fat:${this.#generation}:${hex(partition.offset)}`,
            kind: "dir",
            title: `${partitionTitle(partition)}${read}`,
            children: async () => this.#fatRoot(disk, partition),
        };
    }

    async #fatRoot(disk: string, partition: Partition): Promise<TreeNode[]> {
        const fat = this.#fats.get(partition.offset);
        const id = `device:fat:${hex(partition.offset)}`;
        switch (fat?.state) {
            case undefined:
                void this.#readFat(disk, partition);
                return [note(`${id}:reading`, "Reading…")];
            case "reading":
                return [note(`${id}:reading`, "Reading…")];
            case "failed":
                return [{ ...note(`${id}:failed`, fat.reason), title: fat.reason }];
            case "read":
                return this.#folder(fat, partition, fat.fat.volume.root(), "");
        }
    }

    async #readFat(disk: string, partition: Partition): Promise<void> {
        const board = this.#board;
        const generation = this.#generation;
        this.#fats.set(partition.offset, { state: "reading" });
        let next: FatState;
        try {
            const title = `Reading ${partition.label} at ${hex(partition.offset)}`;
            const bytes = await this.#access.read(disk, partition.offset, partition.size, title);
            next = { state: "read", fat: openFatPartition(bytes), put: new Set() };
        } catch (e) {
            const reason =
                e instanceof NotFat
                    ? `No FAT filesystem: ${e.message}`
                    : `Could not read: ${e instanceof Error ? e.message : String(e)}`;
            next = { state: "failed", reason };
        }
        // What was read before a new board or a refresh is not this board's now.
        if (board === this.#board && generation === this.#generation && this.#fats.has(partition.offset)) {
            this.#fats.set(partition.offset, next);
        } else {
            this.#fats.delete(partition.offset);
        }
        this.#access.changed();
    }

    /** An NVS partition's row opens its entries in a table; the whole partition is read at the first open. */
    #nvsRow(disk: string, partition: Partition): Partial<TreeNode> {
        const nvs = this.#nvs.get(partition.offset);
        const count = nvs?.state === "read" ? nvs.nvs.entries.length : undefined;
        return {
            title: `${partitionTitle(partition)}${count === undefined ? "" : `\n${count} ${count === 1 ? "entry" : "entries"}`}`,
            open: () => {
                if (!this.#nvs.has(partition.offset)) void this.#readNvs(disk, partition);
                this.#access.openNvs({
                    id: `device-nvs:${partition.label}`,
                    title: partition.label,
                    state: () => this.#nvs.get(partition.offset),
                    watch: (redraw) => {
                        this.#nvsTabs.add(redraw);
                        return () => this.#nvsTabs.delete(redraw);
                    },
                    open: (entry) => void this.#openValue(partition, entry),
                });
            },
        };
    }

    async #readNvs(disk: string, partition: Partition): Promise<void> {
        const board = this.#board;
        const generation = this.#generation;
        this.#nvs.set(partition.offset, { state: "reading" });
        this.#nvsChanged();
        let next: NvsState;
        try {
            const title = `Reading ${partition.label} at ${hex(partition.offset)}`;
            next = {
                state: "read",
                nvs: readNvs(await this.#access.read(disk, partition.offset, partition.size, title)),
            };
        } catch (e) {
            const reason =
                e instanceof NotNvs
                    ? `No NVS entries to show: ${e.message}`
                    : `Could not read: ${e instanceof Error ? e.message : String(e)}`;
            next = { state: "failed", reason };
        }
        if (board === this.#board && generation === this.#generation && this.#nvs.has(partition.offset)) {
            this.#nvs.set(partition.offset, next);
        } else {
            this.#nvs.delete(partition.offset);
        }
        this.#nvsChanged();
        this.#access.changed();
    }

    async #openValue(partition: Partition, entry: NvsEntry): Promise<void> {
        const value = entry.value;
        if (typeof value !== "string" && !(value instanceof Uint8Array)) return;
        const file = valueFile({ ...entry, value });
        const path = filePath(partition, file.name);
        await this.#access.putFile(path, file.data);
        this.#access.openFile(path);
    }

    #clearNvs(): void {
        this.#nvs.clear();
        this.#nvsChanged();
    }

    #nvsChanged(): void {
        for (const redraw of this.#nvsTabs) redraw();
    }

    /** A folder's rows, folders first; its files are put where their tabs open them when it is first listed. */
    async #folder(
        read: FatState & { state: "read" },
        partition: Partition,
        entries: FatEntry[],
        path: string,
    ): Promise<TreeNode[]> {
        const volume = read.fat.volume;
        const sorted = [...entries].sort(
            (a, b) =>
                Number(b.directory) - Number(a.directory) ||
                a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        );
        const id = (entry: FatEntry) => `device:fat:${this.#generation}:${hex(partition.offset)}:${path}${entry.name}`;
        if (!read.put.has(path)) {
            const files = sorted.filter((entry) => !entry.directory);
            await Promise.all(
                files.map((entry) => this.#access.putFile(filePath(partition, path + entry.name), volume.read(entry))),
            );
            read.put.add(path);
        }
        return sorted.map((entry) => {
            const title = [
                `${path}${entry.name}`,
                entry.directory ? undefined : `${entry.size} bytes`,
                entry.modified && `modified ${entry.modified}`,
            ];
            const row: TreeNode = {
                id: id(entry),
                label: entry.name,
                kind: entry.directory ? "dir" : "file",
                title: title.filter(Boolean).join("\n"),
            };
            if (entry.directory) {
                row.children = async () => this.#folder(read, partition, volume.list(entry), `${path}${entry.name}/`);
            } else {
                row.path = filePath(partition, path + entry.name);
            }
            return row;
        });
    }
}

function partitionTitle(partition: Partition): string {
    return [
        `${partition.label}: ${typeName(partition.type)}, ${subtypeName(partition.type, partition.subtype)}`,
        `offset ${hex(partition.offset)}, size ${formatSize(partition.size)} (${hex(partition.size)})`,
        ...(partition.encrypted ? ["encrypted"] : []),
        ...(partition.readonly ? ["read-only"] : []),
    ].join("\n");
}

function isFat(partition: Partition): boolean {
    return partition.type === 1 && partition.subtype === 0x81;
}

function isNvs(partition: Partition): boolean {
    return partition.type === 1 && partition.subtype === 0x02;
}

function filePath(partition: Partition, path: string): string {
    return `${DEVICE_FILES}/${partition.label}/${path}`;
}

/**
 * The table at the build's offset or ESP-IDF's default; failing both, the
 * first one found in the sectors before `SCAN_END`.
 */
async function readTable(access: DeviceAccess, disk: string): Promise<TableState> {
    const offsets = [...new Set([(await access.tableOffset()) ?? DEFAULT_TABLE_OFFSET, DEFAULT_TABLE_OFFSET])];
    for (const offset of offsets) {
        try {
            const bytes = await access.read(disk, offset, TABLE_LENGTH, `Reading partitions at ${hex(offset)}`);
            const table = parsePartitionTable(bytes);
            return { state: "read", offset, table };
        } catch (e) {
            if (!(e instanceof NotATable)) throw e;
        }
    }
    const scan = "Scanning for partitions";
    const found = findPartitionTable(await access.read(disk, SECTOR, SCAN_END - SECTOR, scan), SECTOR);
    if (found === undefined) return { state: "failed", reason: `no partition table below ${hex(SCAN_END)}` };
    return { state: "read", ...found };
}

function note(id: string, label: string): TreeNode {
    return { id, label, kind: "file", dim: true };
}
